import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Catches render errors so a failed page/component shows a recovery UI
 * with the actual error message instead of a blank screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    const key = 'provisoft_error_reload';
    const last = sessionStorage.getItem(key);
    const now = Date.now();
    if (!last || now - Number(last) > 30000) {
      sessionStorage.setItem(key, String(now));
      const url = new URL(window.location.href);
      url.searchParams.set('_v', String(Math.floor(now / 1000)));
      window.location.replace(url.toString());
      return;
    }
    window.location.href = '/login';
  };

  handleDismiss = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo } = this.state;
    const message = error?.message || String(error || 'Unknown error');
    const stack = error?.stack || '';
    const componentStack = errorInfo?.componentStack || '';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg border p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold text-gray-900">Application error</h1>
              <p className="text-sm text-red-700 font-mono mt-1 break-words">{message}</p>
            </div>
          </div>

          {(stack || componentStack) && (
            <details className="rounded-lg border bg-slate-50 text-left">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">
                Technical details
              </summary>
              <div className="px-3 pb-3 space-y-2 max-h-64 overflow-auto">
                {stack && (
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap break-words font-mono">{stack}</pre>
                )}
                {componentStack && (
                  <pre className="text-xs text-slate-500 whitespace-pre-wrap break-words font-mono border-t pt-2">
                    {componentStack.trim()}
                  </pre>
                )}
              </div>
            </details>
          )}

          <p className="text-sm text-gray-600">
            If this appeared right after a deploy, try reloading — your browser may have cached an old bundle.
          </p>

          <div className="flex gap-2">
            <Button onClick={this.handleReload} className="flex-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload application
            </Button>
            {this.props.fallbackDismissible && (
              <Button variant="outline" onClick={this.handleDismiss}>
                Dismiss
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
